import { 
  bmiStatus, 
  // FLUID_INTAKE_LEVELS, 
  // PHYSICAL_ACTIVITY_LEVELS, 
  // HB_LEVELS,
  // WAIST_CIRCUMFERENCE_LEVELS,
  // RANDOM_BLOOD_SUGAR_LEVELS,
  // FASTING_BLOOD_SUGAR_LEVELS,
  // LDL_LEVELS,
  // HDL_LEVELS,
  // TOTAL_CHOLESTEROL_LEVELS,
  // URIC_ACID_LEVELS,
  // HBA1C_LEVELS,
} from '@/constants/meal-plans'
import { parseFloatToFixed2 } from '@/utils/other'

export const nutritionalStatusAdult = {
  calculateImt(weight, height) {
    height = height / 100
    const result = weight / (height * height)

    return this.getRoundedResult(parseFloat(result)).toFixed(2)
  },

  getBmiStatus(imt) {
    let result

    // Rule based on BMI value
    if (imt < 18.5) {
      result = bmiStatus.BERAT_BADAN_KURANG
    } else if (imt < 23) {
      result = bmiStatus.NORMAL
    } else if (imt < 25) {
      result = bmiStatus.PREOBESITAS
    } else if (imt < 30) {
      result = bmiStatus.OBESITAS_1
    } else {
      result = bmiStatus.OBESITAS_2
    }

    return result
  },

  getIdealWeightMinimum(height) {
    height = height / 100
    const minimum = parseFloat(18.5 * (height * height))
    return Math.round(minimum)
  },

  getIdealWeightMaximum(height) {
    height = height / 100
    const maximum = parseFloat(22.9 * (height * height))
    return Math.round(maximum)
  },

  getIdealWeightMedian(height) {
    const minimum = this.getIdealWeightMinimum(height)
    const maximum = this.getIdealWeightMaximum(height)

    const median = parseFloat(((minimum + maximum) / 2))

    return this.getRoundedResult(median).toFixed(2)
  },

  getBroccaIdealWeight(height, gender) {
    let result

    if (gender === 'female') {
      if (height < 150) {
        result = parseFloat((height - 100) * 1)
      } else {
        result = parseFloat(0.9 * (height - 100) * 1)
      }
    } else {
      if (height < 160) {
        result = parseFloat((height - 100) * 1)
      } else {
        result = parseFloat(0.9 * (height - 100) * 1)
      }
    }

    return this.getRoundedResult(parseFloat(result)).toFixed(2)
  },

  getIdealWeightAdjusted(weight, height, gender) {
    const broccaIdealWeight = parseFloat(this.getBroccaIdealWeight(height, gender))
    const result = this.getRoundedResult(((parseFloat(weight) - broccaIdealWeight) * 0.25) + broccaIdealWeight)

    return result.toFixed(2)
  },

  getRoundedResult(number) {
    return (parseFloat(number) * 100 + 0.05) / 100
  },
}

export const mifflinStJeorFormula = {
  bassEnergyRequirement(age, gender, weight, height) {
    let constants

    if (gender === 'female') {
      constants = -161
    } else {
      constants = 5
    }

    const result = parseFloat(10 * weight + 6.25 * height - 5 * age + constants)

    return Math.round(result)
  },

  totalEnergyNeeded(
    age,
    gender,
    weight,
    height,
    physicalActivity,
    maternalCondition,
    calorieDeficit,
  ) {
    const bassEnergyRequirement = this.bassEnergyRequirement(
      age,
      gender,
      weight,
      height,
    )

    physicalActivity = physicalActivity / 100

    const result =
      bassEnergyRequirement +
      bassEnergyRequirement * physicalActivity +
      maternalCondition -
      calorieDeficit

    return Math.round(result)
  },
}

export const perkeniFormula = {
  bassEnergyRequirement(gender, weight) {
    let constants

    if (gender === 'female') {
      constants = 25
    } else {
      constants = 30
    }

    const result = parseFloat(constants * weight)

    return Math.round(result)
  },

  totalEnergyNeeded(
    gender,
    weight,
    physicalActivity,
    ageFactor,
    metabolicStressFactor,
    malnutritionStatusFactor,
    overnutritionStatusFactor,
    maternalCondition,
  ) {
    physicalActivity = physicalActivity / 100
    ageFactor = ageFactor / 100
    metabolicStressFactor = metabolicStressFactor / 100
    malnutritionStatusFactor = malnutritionStatusFactor / 100
    overnutritionStatusFactor = overnutritionStatusFactor / 100

    const bassEnergyRequirement = this.bassEnergyRequirement(gender, weight)

    const result = parseFloat(
      bassEnergyRequirement +
      (physicalActivity -
        ageFactor +
        metabolicStressFactor +
        malnutritionStatusFactor -
        overnutritionStatusFactor) *
      bassEnergyRequirement +
      maternalCondition,
    )

    return Math.round(result)
  },
}

export const ckdFormula = {
  totalEnergyNeeded(weight, ckdEnergyRequirement) {
    const result = parseFloat(weight * ckdEnergyRequirement)

    return Math.round(result)
  },
}

export const nutritionalStatusChild = {
  calculateImt(weight, correctedHeight) {
    correctedHeight = correctedHeight / 100
    const result = weight / (correctedHeight * correctedHeight)

    return parseFloat(result).toFixed(2)
  },
}

export const totalEnergyChild = {
  totalEnergyNeeded(constants, weightIdeal) {
    const result = parseFloat(constants * weightIdeal)

    return Math.round(result)
  },

  totalEnergyNeeded2(age, type, gender, weight) {
    let constants

    if (type === 'month') {
      constants = age < 6 ? 108 : 98
    } else {
      if (age < 4) constants = 102
      else if (age < 7) constants = 90
      else if (age < 11) constants = 70
      else if (age < 15 && gender === 'female') constants = 47
      else if (age < 19 && gender === 'female') constants = 40
      else if (age < 15 && gender === 'male') constants = 55
      else if (age < 19 && gender === 'male') constants = 45
    }

    const result = parseFloat(constants * weight)

    return Math.round(result)
  },
}

export const energySufficiencyFormula = {
  energySufficiency(totalEnergy, energyIntake) {
    const result = parseFloat((energyIntake / totalEnergy) * 100)

    return Math.round(result)
  },

  energyStatus(totalEnergy, energyIntake) {
    const energySufficiency = this.energySufficiency(totalEnergy, energyIntake)
    let result

    if (energySufficiency < 80) result = 'Asupanmu kurang'
    else if (energySufficiency <= 110) result = 'Asupanmu cukup'
    else result = 'Asupanmu berlebih'

    return result
  },
}

export const proteinIntakeFormula = {
  proteinRequirement(weight, proteinPerWeight) {
    const result = parseFloat(weight * proteinPerWeight)

    return parseFloatToFixed2(result)
  },

  proteinRequirementConstant(weight, proteinPerWeight, totalEnergy) {
    const proteinRequirement = this.proteinRequirement(
      weight,
      proteinPerWeight,
    )

    const result = parseFloat((proteinRequirement * 4) / totalEnergy)

    return parseFloatToFixed2(result)
  },

  proteinAdequacy(weight, proteinPerWeight, currentProtein) {
    const proteinRequirement = this.proteinRequirement(
      weight,
      proteinPerWeight,
    )

    const result = parseFloat((currentProtein / proteinRequirement) * 100)

    return parseFloatToFixed2(result)
  },

  proteinStatus(weight, proteinPerWeight, currentProtein) {
    const proteinAdequacy = this.proteinAdequacy(
      weight,
      proteinPerWeight,
      currentProtein,
    )

    let result

    if (proteinAdequacy < 80) result = 'Asupanmu kurang'
    else if (proteinAdequacy <= 110) result = 'Asupanmu cukup'
    else result = 'Asupanmu berlebih'

    return result
  },
}

export const fatIntakeFormula = {
  fatRequirement(totalEnergy, fatRequirement) {
    fatRequirement = fatRequirement / 100

    const result = parseFloat(totalEnergy * fatRequirement) / 9

    return Math.round(result)
  },

  fatAdequacy(totalEnergy, fatRequirement, currentFat) {
    const fatRequirementFormula = this.fatRequirement(
      totalEnergy,
      fatRequirement,
    )
    const result = parseFloat((currentFat / fatRequirementFormula) * 100)

    return Math.round(result)
  },

  fatStatus(totalEnergy, fatRequirement, currentFat) {
    const fatAdequacy = this.fatAdequacy(
      totalEnergy,
      fatRequirement,
      currentFat,
    )

    let result

    if (fatAdequacy < 80) result = 'Asupanmu kurang'
    else if (fatAdequacy <= 110) result = 'Asupanmu cukup'
    else result = 'Asupanmu berlebih'

    return result
  },
}

export const carbohydrateIntakeFormula = {
  carbohydrateRequirement(totalEnergy, proteinConstant, sufficientFat) {
    sufficientFat = sufficientFat / 100

    const result = parseFloat(
      (totalEnergy * (1 - proteinConstant - sufficientFat / 1)) / 4,
    )

    return Math.round(result)
  },

  carbohydrateAdequacy(
    totalEnergy,
    proteinConstant,
    sufficientFat,
    currentCarbohydrate,
  ) {
    const carbohydrateRequirement = this.carbohydrateRequirement(
      totalEnergy,
      proteinConstant,
      sufficientFat,
    )

    const result = parseFloat(
      (currentCarbohydrate / carbohydrateRequirement) * 100,
    )

    return Math.round(result)
  },

  carbohydrateStatus(
    totalEnergy,
    proteinConstant,
    sufficientFat,
    currentCarbohydrate,
  ) {
    const carbohydrateAdequacy = this.carbohydrateAdequacy(
      totalEnergy,
      proteinConstant,
      sufficientFat,
      currentCarbohydrate,
    )

    let result

    if (carbohydrateAdequacy < 80) result = 'Asupanmu kurang'
    else if (carbohydrateAdequacy <= 110) result = 'Asupanmu cukup'
    else result = 'Asupanmu berlebih'

    return result
  },
}

export const fiberIntakeFormula = {
  fiberAdequacy(fiberRequirement, currentFiber) {
    const result = parseFloat((currentFiber / fiberRequirement) * 100)

    return Math.round(result)
  },

  fiberStatus(fiberRequirement, currentFiber) {
    const fiberAdequacy = this.fiberAdequacy(fiberRequirement, currentFiber)

    let result

    if (fiberAdequacy < 80) result = 'Asupanmu kurang'
    else if (fiberAdequacy <= 110) result = 'Asupanmu cukup'
    else result = 'Asupanmu berlebih'

    return result
  },
}

export const bodyConditionFormula = {
  /**
   * Checks if the water intake is sufficient based on age, gender, and pregnancy status.
   * @param {number} [value=0] - Amount of water intake.
   * @param {number} age - Age of the individual in years.
   * @param {'male'|'female'} [gender='male'] - Gender.
   * @param {boolean} [isPregnant=false] - Pregnancy status.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkWaterIntake(value = 0, age, gender = 'male', isPregnant = false, FLUID_INTAKE_LEVELS) {
    let standardInfo
  
    // Cek kondisi khusus (kehamilan)
    if (isPregnant) {
      standardInfo = FLUID_INTAKE_LEVELS.standard.find(item => item.special === 'pregnant')
    } else {
    // Cari standar yang sesuai berdasarkan usia dan gender
      standardInfo = FLUID_INTAKE_LEVELS.standard.find(item => 
        age >= item.ageMin && 
      age <= item.ageMax && 
      item.gender.includes(gender.toLowerCase()),
      )
    }
  
    const threshold = standardInfo.value
    const unit = FLUID_INTAKE_LEVELS.unit[standardInfo.unit]
  
    const status = value >= threshold 
      ? FLUID_INTAKE_LEVELS.status.sufficient 
      : FLUID_INTAKE_LEVELS.status.insufficient
  
    return {
      status,
      requirement: `≥${threshold}`,
      unit,
      actual: value,
    }
  },

  /**
   * Checks if the weekly physical activity duration is sufficient.
   * @param {number} [value=0] - The total minutes of physical activity per week.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkPhysicalActivity(value = 0, PHYSICAL_ACTIVITY_LEVELS) {
    const status = value >= PHYSICAL_ACTIVITY_LEVELS.standard ? 
      PHYSICAL_ACTIVITY_LEVELS.status.sufficient : 
      PHYSICAL_ACTIVITY_LEVELS.status.insufficient
  
    return {
      status,
      requirement: `≥${PHYSICAL_ACTIVITY_LEVELS.standard}`,
      unit: PHYSICAL_ACTIVITY_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks waist circumference based on gender.
   * @param {number} [value=0] - Waist circumference in cm.
   * @param {'male'|'female'} gender - Gender.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkWaistCircumference(value = 0, gender, WAIST_CIRCUMFERENCE_LEVELS) {
    const maxNormal = gender.toLowerCase() === 'female' ? 
      WAIST_CIRCUMFERENCE_LEVELS.standard.female : 
      WAIST_CIRCUMFERENCE_LEVELS.standard.male
    
    const status = value <= maxNormal ? 
      WAIST_CIRCUMFERENCE_LEVELS.status.normal : 
      WAIST_CIRCUMFERENCE_LEVELS.status.excess
  
    return {
      status,
      requirement: `≤${maxNormal}`,
      unit: WAIST_CIRCUMFERENCE_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks random blood sugar level.
   * @param {number} [value=0] - Random blood sugar level.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkRandomBloodSugar(value = 0, RANDOM_BLOOD_SUGAR_LEVELS) {
    let status
  
    if (value < RANDOM_BLOOD_SUGAR_LEVELS.standard.normal.max + 1) {
      status = RANDOM_BLOOD_SUGAR_LEVELS.status.normal
    } else {
      status = RANDOM_BLOOD_SUGAR_LEVELS.status.high
    }
  
    return {
      status,
      requirement: `<${RANDOM_BLOOD_SUGAR_LEVELS.standard.normal.max + 1}`,
      unit: RANDOM_BLOOD_SUGAR_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks fasting blood sugar level.
   * @param {number} [value=0] - Fasting blood sugar level.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkFastingBloodSugar(value = 0, FASTING_BLOOD_SUGAR_LEVELS) {
    let status
  
    if (value >= FASTING_BLOOD_SUGAR_LEVELS.standard.normal.min && 
      value <= FASTING_BLOOD_SUGAR_LEVELS.standard.normal.max) {
      status = FASTING_BLOOD_SUGAR_LEVELS.status.normal
    } else if (value >= FASTING_BLOOD_SUGAR_LEVELS.standard.prediabetes.min && value <= FASTING_BLOOD_SUGAR_LEVELS.standard.prediabetes.max) {
      status = FASTING_BLOOD_SUGAR_LEVELS.status.prediabetes
    } else if (value >= FASTING_BLOOD_SUGAR_LEVELS.standard.diabetes.min) {
      status = FASTING_BLOOD_SUGAR_LEVELS.status.diabetes
    } else {
      // Untuk nilai < 70 mg/dL masuk ke kategori "N/A"
      status = FASTING_BLOOD_SUGAR_LEVELS.status.na
    }
  
    return {
      status,
      requirement: `${FASTING_BLOOD_SUGAR_LEVELS.standard.normal.min} - ${FASTING_BLOOD_SUGAR_LEVELS.standard.normal.max}`,
      unit: FASTING_BLOOD_SUGAR_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks LDL cholesterol level.
   * @param {number} [value=0] - LDL level.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkLDL(value = 0, LDL_LEVELS) {
    let status
  
    if (value < LDL_LEVELS.standard.optimal.max + 1) {
      status = LDL_LEVELS.status.optimal
    } else if (value >= LDL_LEVELS.standard.nearOptimal.min && value <= LDL_LEVELS.standard.nearOptimal.max) {
      status = LDL_LEVELS.status.nearOptimal
    } else if (value >= LDL_LEVELS.standard.borderlineHigh.min && value <= LDL_LEVELS.standard.borderlineHigh.max) {
      status = LDL_LEVELS.status.borderlineHigh
    } else if (value >= LDL_LEVELS.standard.high.min && value <= LDL_LEVELS.standard.high.max) {
      status = LDL_LEVELS.status.high
    } else {
      status = LDL_LEVELS.status.veryHigh
    }

    return {
      status,
      requirement: `<${LDL_LEVELS.standard.optimal.max + 1}`,
      unit: LDL_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks HDL cholesterol level.
   * @param {number} [value=0] - HDL level.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkHDL(value = 0, HDL_LEVELS) {
    let status
    
    if (value < HDL_LEVELS.standard.low) {
      status = HDL_LEVELS.status.low
    } else if (value >= HDL_LEVELS.standard.low && value < HDL_LEVELS.standard.high) {
      status = HDL_LEVELS.status.normal
    } else {
      status = HDL_LEVELS.status.high
    }

    return {
      status,
      requirement: `${HDL_LEVELS.standard.normal.min} - ${HDL_LEVELS.standard.normal.max}`,
      unit: HDL_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks total cholesterol level.
   * @param {number} [value=0] - Total cholesterol.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkTotalCholesterol(value = 0, TOTAL_CHOLESTEROL_LEVELS) {
    let status
  
    if (value < TOTAL_CHOLESTEROL_LEVELS.standard.desirable.max + 1) {
      status = TOTAL_CHOLESTEROL_LEVELS.status.desirable
    } else if (value >= TOTAL_CHOLESTEROL_LEVELS.standard.borderlineHigh.min && value <= TOTAL_CHOLESTEROL_LEVELS.standard.borderlineHigh.max) {
      status = TOTAL_CHOLESTEROL_LEVELS.status.borderlineHigh
    } else {
      status = TOTAL_CHOLESTEROL_LEVELS.status.high
    }
  
    return {
      status,
      requirement: `<${TOTAL_CHOLESTEROL_LEVELS.standard.desirable.max + 1}`,
      unit: TOTAL_CHOLESTEROL_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks uric acid level.
   * @param {number} [value=0] - Uric acid level.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkUricAcid(value = 0, URIC_ACID_LEVELS) {
    let status
  
    if (value < URIC_ACID_LEVELS.standard.normal.max) {
      status = URIC_ACID_LEVELS.status.normal
    } else {
      status = URIC_ACID_LEVELS.status.high
    }
  
    return {
      status,
      requirement: `<${URIC_ACID_LEVELS.standard.normal.max}`,
      unit: URIC_ACID_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks HbA1C level.
   * @param {number} [value=0] - HbA1C value.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkHbA1C(value = 0, HBA1C_LEVELS) {
    let status
  
    if (value < HBA1C_LEVELS.standard.normal.max + 0.1) {
      status = HBA1C_LEVELS.status.normal
    } else if (value >= HBA1C_LEVELS.standard.prediabetes.min && value <= HBA1C_LEVELS.standard.prediabetes.max) {
      status = HBA1C_LEVELS.status.prediabetes
    } else {
      status = HBA1C_LEVELS.status.diabetes
    }
  
    return {
      status,
      requirement: `<${HBA1C_LEVELS.standard.prediabetes.min}`,
      unit: HBA1C_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Checks hemoglobin level based on age, gender, and pregnancy status.
   * @param {number} [value=0] - Hemoglobin level.
   * @param {number} age - Age of the individual in years.
   * @param {'male'|'female'} [gender='male'] - Gender.
   * @param {string|null} [trimester=null] - Pregnancy 'Hamil Trimester 1', 'Hamil Trimester 2', 'Hamil Trimester 3', null if not pregnant.
   * @returns {{ status: string, requirement: string, unit: string, actual: number }}
   */
  checkHbLevel(value = 0, age, gender = 'male', trimester = null, HB_LEVELS) {
    // Cek kondisi untuk usia di bawah 6 bulan (0.5 tahun)
    if (age < 0.5) {
      return {
        status: HB_LEVELS.status.na,
        requirement: 'N/A',
        unit: HB_LEVELS.unit,
        actual: value,
      }
    }

    let threshold
  
    // Cek kondisi kehamilan
    if (trimester !== null && gender.toLowerCase() === 'female') {
      const pregnancyStandard = HB_LEVELS.standard.find(item => 
        item.special === 'pregnancy' && item.trimester === trimester,
      )
      threshold = pregnancyStandard.value
    } 
    // Cek kondisi berdasarkan usia dan jenis kelamin
    else {
      const standardInfo = HB_LEVELS.standard.find(item => {
        // Mengecek apakah usia berada dalam rentang minimum dan maksimum item
        const isInAgeRange = age >= item.ageMin && age <= item.ageMax
      
        // Mengecek apakah gender pengguna termasuk dalam array gender yang didukung oleh item
        const isGenderMatch = item.gender && item.gender.includes(gender.toLowerCase())
      
        return isInAgeRange && isGenderMatch
      })

      threshold = standardInfo.value
    }
  
    const status = value >= threshold 
      ? HB_LEVELS.status.normal 
      : HB_LEVELS.status.low
  
    return {
      status,
      requirement: `≥${threshold}`,
      unit: HB_LEVELS.unit,
      actual: value,
    }
  },

  /**
   * Function to get the color code based on the status text.
   * @param {string} status - The status text to evaluate
   * @returns {string} - The corresponding color code
   */
  getStatusColor(status) {
    if (!status) {
      return {
        background: '#F2F2F2',
        color: '#000000',
      }
    }

    const statusLower = status.toLowerCase()

    // Merah
    if (['kurang', 'berlebih', 'diabetes', 'tinggi', 'sangat tinggi'].includes(statusLower)) {
      return {
        background: '#FCDED3',
        color: '#D32737',
      }
    }
  
    // Kuning
    if (['rendah', 'pre-diabetes', 'mendekati optimal', 'sedikit tinggi'].includes(statusLower)) {
      return {
        background: '#FDEDCB',
        color: '#DA6D06',
      }
    }
  
    // Hijau
    if (['cukup', 'normal', 'diinginkan', 'optimal'].includes(statusLower)) {
      return {
        background: '#C7F9E3',
        color: '#008C81',
      }
    }
  
    // Gray status
    if (['tidak ada data', 'n/a'].includes(statusLower)) {
      return {
        background: '#C4C4C4',
        color: '#000000',
      }
    }
  
    // Light Gray for custom status
    return {
      background: '#F2F2F2',
      color: '#00000',
    }
  },
}